import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

@Component({
  selector: 'app-pdf-viewer-dialog',
  templateUrl: './pdf-viewer-dialog.component.html',
  styleUrls: ['./pdf-viewer-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, NgxExtendedPdfViewerModule],
})
export class PdfViewerDialogComponent {
  // Provide raw PDF data (Uint8Array) for ngx-extended-pdf-viewer to consume directly
  public pdfData: ArrayBuffer | null = null;
  // Provide the raw base64 string (without data: prefix) and data URI for ngx-extended-pdf-viewer
  public pdfBase64Raw: string | null = null;
  public pdfBase64String: string | null = null;
  // Blob URL / sanitized resource URL for embedding via <object>
  public pdfUrl: string | null = null;
  public pdfResourceUrl: SafeResourceUrl | null = null;
  // Flag to indicate loading/parsing error for base64 input
  public pdfLoadError: boolean = false;
  public previewErrorMessage: string | null = null;
  public previewUrl: string | null = null;
  public editing = false;
  private originalBase64: string | null = null;
  private editedBase64: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PdfViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
  ) {
    this.previewUrl = data?.url ?? data?.secureUrl ?? data?.sourceUrl ?? null;
    this.previewErrorMessage = data?.errorMessage ?? null;
    if (data && data.base64) {
      this.originalBase64 = data.base64;
      this.loadPreviewFromBase64(data.base64);
    } else {
      this.pdfLoadError = true;
      this.previewErrorMessage = this.previewErrorMessage || 'The generated PDF is not available or could not be loaded.';
    }
  }

  private loadPreviewFromBase64(b64: string) {
    try {
      // sanitize base64 input: strip data URI prefix if present and remove whitespace/newlines
      const stripDataPrefix = (s: string): string => {
        if (!s) return s;
        const m = s.match(/^data:([\w/+.-]+);base64,(.*)$/i);
        if (m && m[2]) return m[2];
        return s;
      };
      const normalizeBase64 = (s: string): string => {
        let out = stripDataPrefix(s).replace(/\s+/g, '');
        // convert URL-safe base64 to standard
        out = out.replace(/-/g, '+').replace(/_/g, '/');
        // pad with '=' to multiple of 4
        const mod = out.length % 4;
        if (mod > 0) out += '='.repeat(4 - mod);
        return out;
      };
      let sanitized = normalizeBase64(b64 || '');
      // Additional cleaning: remove surrounding quotes or backslashes introduced by JSON/stringification
      if (sanitized.startsWith('\"') && sanitized.endsWith('\"')) {
        sanitized = sanitized.slice(2, -2);
      }
      if (sanitized.startsWith('"') && sanitized.endsWith('"')) {
        sanitized = sanitized.slice(1, -1);
      }
      sanitized = sanitized.replace(/\\n/g, '').replace(/\\r/g, '').replace(/\s+/g, '');

      // Base64 validation helper
      const isValidBase64 = (s: string) => {
        if (!s) return false;
        // allow standard base64 characters and optional padding
        return /^[A-Za-z0-9+/]+={0,2}$/.test(s);
      };

      if (!isValidBase64(sanitized)) {
        // Try URL-safe to standard conversion (already done) and decode URI component if present
        try {
          const maybeDecoded = decodeURIComponent(sanitized);
          if (isValidBase64(maybeDecoded)) {
            sanitized = maybeDecoded;
          }
        } catch {
          /* ignore */
        }
      }

      if (!isValidBase64(sanitized)) {
        console.error('[PdfViewerDialog] base64 appears invalid after sanitization — will not pass to viewer.');
        this.pdfLoadError = true;
        // keep pdfBase64String for download fallback using original input
        this.pdfBase64String = b64 ? (b64.startsWith('data:') ? b64 : `data:application/pdf;base64,${b64}`) : null;
        return;
      }

      // Convert base64 to Uint8Array and hand it directly to the viewer
      const bytes = this.base64ToUint8Array(sanitized);
      this.pdfData = bytes.buffer as ArrayBuffer;
      // Store raw base64 (sanitized) and data URI for viewer binding
      this.pdfBase64Raw = sanitized;
      this.pdfBase64String = `data:application/pdf;base64,${sanitized}`;
      this.pdfLoadError = false;
      console.debug(
        '[PdfViewerDialog] loaded PDF bytes, bytes.length=',
        bytes?.length,
        ' base64Raw.length=',
        this.pdfBase64Raw?.length,
        ' dataUri.length=',
        this.pdfBase64String?.length,
      );
      // Also prepare a blob URL fallback that some environments may require
      try {
        const blob = new Blob([bytes.buffer as unknown as ArrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.pdfUrl = url;
        try {
          this.pdfResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } catch {
          this.pdfResourceUrl = null;
        }
        // Revoke after a delay
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }, 60000);
      } catch (e) {
        console.warn('[PdfViewerDialog] failed to create blob URL fallback', e);
      }
    } catch (e) {
      console.error('Failed to create preview from base64', e);
    }
  }

  close(result?: any) {
    this.dialogRef.close(result);
  }

  async addTextStamp(text: string) {
    if (!this.originalBase64) return;
    try {
      const existingPdfBytes = this.base64ToUint8Array(this.originalBase64);
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const pageSize = firstPage.getSize();
      const height = pageSize.height; // only height is used
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      firstPage.drawText(text, {
        x: 20,
        y: height - 40 - 10 * (pages.length - 1),
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      const modified = await pdfDoc.save();
      const b64 = this.uint8ArrayToBase64(modified);
      this.editedBase64 = b64;
      this.loadPreviewFromBase64(b64);
      this.editing = true;
    } catch (err) {
      console.error('Failed to add text stamp', err);
    }
  }

  onSaveEditedPdf() {
    const out = this.editedBase64 || this.originalBase64;
    this.close({ base64: out });
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
